import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { configurationManager, i18NManager } from "../../../../shared/core/service/services";
import { useObservable } from "../../../../shared/utils/observable";
import { ACCOUNT_DELETE_MODAL_ID, PINCODE_MUST_BE_CHANGED_MODAL_ID } from "../../../core/modal/modal-id";
import { Path } from "../../../core/routing/path";
import { changeLanguage } from "../../../domain/language/language";
import { getLanguageName } from "../../../utils/translations";
import { ActionRow, ActionRowList, actionRowStyle } from "../../common/action-row";
import { SelectInput } from "../../common/forms/select-input";
import { Modal } from "../../common/modal/modal";
import { PageBackLink } from "../../common/page/page-back-header";
import { theme } from "../../styles/theme";
import { PincodeChangeModal } from "../pincode/pincode-change-modal";
import { AccountDeleteModal } from "./components/account-delete-modal";

/* The select paints no border of its own; in a row it needs one to read as a field. */
const LANGUAGE_SELECT_STYLE = {
  control: (provided) => ({
    ...provided,
    minHeight: "40px",
    border: `1px solid ${theme.colors.gray[300]}`,
  }),
};

export const MySettingsScreen = () => {
  const { formatMessage } = useIntl();
  const { push } = useHistory();
  const userLanguage = useObservable(i18NManager.localeTag);
  const configurationLanguages = useObservable(configurationManager.configuration).langs;

  return (
    <>
      <PageBackLink onBack={() => push(Path.ProfileMenu)} />
      <Panel>
        <PanelTitle>{formatMessage("settings.title")}</PanelTitle>
        <ActionRowList>
          <ActionRow
            label={formatMessage("settings.changePincodeTitle")}
            subtitle={formatMessage("settings.changePincodeDescription")}
            onClick={() =>
              Modal.present(PINCODE_MUST_BE_CHANGED_MODAL_ID, () => <PincodeChangeModal type="skip-explanation" />, {
                canBeDismissed: false,
              })
            }
          />
          {/* The language is picked in place: the row carries the choice rather than a way out. */}
          <LanguageRow>
            <Texts>
              <RowLabel>{formatMessage("settings.changeLanguageTitle")}</RowLabel>
              <RowSubtitle>{formatMessage("settings.changeLanguageDescription")}</RowSubtitle>
            </Texts>
            <LanguageSelect
              value={userLanguage}
              innerId="language"
              withPointer
              options={configurationLanguages}
              itemRenderer={getLanguageName}
              selectStyle={LANGUAGE_SELECT_STYLE}
              onChange={(language) => changeLanguage(language)}
            />
          </LanguageRow>
          <ActionRow
            label={formatMessage("settings.accountDeleteTitle")}
            subtitle={formatMessage("settings.accountDeleteDescription")}
            onClick={() => {
              Modal.present(ACCOUNT_DELETE_MODAL_ID, () => <AccountDeleteModal />, {
                canBeDismissed: false,
              });
            }}
          />
        </ActionRowList>
      </Panel>
    </>
  );
};

/* One white surface holding the list, as the mockup draws it. */
const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-self: stretch;
  width: 100%;
  max-width: 560px;
  margin-top: 12px;
  margin-bottom: 20px;
  padding: 28px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 16px;
  background-color: #ffffff;
`;

const PanelTitle = styled.h1`
  ${theme.boldText};
  margin: 0;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const LanguageRow = styled.div`
  ${actionRowStyle};
  cursor: default;

  &:hover {
    background-color: transparent;
  }
`;

const Texts = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const RowLabel = styled.span`
  ${theme.text};
  font-size: 16px;
  line-height: 22px;
  color: ${theme.colors.blue[800]};
`;

const RowSubtitle = styled.span`
  ${theme.text};
  margin-top: 2px;
  font-size: 12px;
  line-height: 15px;
  color: ${theme.colors.gray[800]};
`;

const LanguageSelect = styled(SelectInput<string>)`
  flex-shrink: 0;
  width: 140px;
`;
